import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineArrowLeft, HiOutlinePlay, HiOutlineMagnifyingGlass, HiOutlineAdjustmentsHorizontal, HiOutlineEye, HiOutlineClock, HiOutlineCheckCircle, HiOutlineAcademicCap, HiOutlineChartBar, HiOutlineArrowTrendingUp, HiOutlineShieldCheck, HiOutlineLightBulb, HiOutlineCube, HiOutlineRocketLaunch } from 'react-icons/hi2';
import './AdvancedLearning.css';

const videoData = [
  {
    id: 1, title: 'Advanced Technical Analysis Concepts', category: 'Technical Analysis', level: 'Advanced',
    views: 12400, timeAgo: '2 weeks ago', duration: '18:45', color: '#a855f7',
    desc: 'Deep dive into advanced chart patterns and technical indicators for professional traders.'
  },
  {
    id: 2, title: 'Options Trading Masterclass', category: 'Options Trading', level: 'Advanced',
    views: 9800, timeAgo: '3 days ago', duration: '24:30', color: '#3b82f6',
    desc: 'Complete guide to options strategies, Greeks, and risk management.'
  },
  {
    id: 3, title: 'Risk Management Strategies', category: 'Risk Management', level: 'Intermediate',
    views: 8500, timeAgo: '1 week ago', duration: '21:15', color: '#10b981',
    desc: 'Learn to protect your capital with proven risk management frameworks.'
  },
  {
    id: 4, title: 'Market Structure Deep Dive', category: 'Market Basics', level: 'Advanced',
    views: 7200, timeAgo: '5 days ago', duration: '16:20', color: '#f59e0b',
    desc: 'Understanding market microstructure, order flow and price discovery.'
  },
  {
    id: 5, title: 'Trading Psychology for Consistency', category: 'Trading Psychology', level: 'Intermediate',
    views: 11200, timeAgo: '1 week ago', duration: '19:10', color: '#ec4899',
    desc: 'Master your emotions and develop a winning trader mindset.'
  },
  {
    id: 6, title: 'Price Action Mastery', category: 'Technical Analysis', level: 'Advanced',
    views: 6300, timeAgo: '2 weeks ago', duration: '23:00', color: '#6366f1',
    desc: 'Read price action like a professional without indicators.'
  },
  {
    id: 7, title: 'Volume Analysis Secrets', category: 'Technical Analysis', level: 'Intermediate',
    views: 5400, timeAgo: '2 weeks ago', duration: '14:30', color: '#0ea5e9',
    desc: 'Decode volume patterns to confirm trends and spot reversals.'
  },
  {
    id: 8, title: 'Multi Timeframe Analysis', category: 'Advanced Strategies', level: 'Advanced',
    views: 4800, timeAgo: '3 weeks ago', duration: '15:55', color: '#a855f7',
    desc: 'Align multiple timeframes for higher probability setups.'
  },
  {
    id: 9, title: 'Fundamental Analysis Complete Guide', category: 'Fundamental Analysis', level: 'Beginner',
    views: 15600, timeAgo: '3 weeks ago', duration: '20:45', color: '#10b981',
    desc: 'Learn to analyze company financials, valuations and economic indicators.'
  },
  {
    id: 10, title: 'Advanced Indicator Strategies', category: 'Technical Analysis', level: 'Advanced',
    views: 7100, timeAgo: '1 month ago', duration: '13:30', color: '#f59e0b',
    desc: 'Combine indicators for high-probability confluent signals.'
  },
  {
    id: 11, title: 'High Probability Trading Setups', category: 'Advanced Strategies', level: 'Advanced',
    views: 9200, timeAgo: '1 month ago', duration: '18:05', color: '#ec4899',
    desc: 'Identify and execute setups with the highest win rates.'
  },
  {
    id: 12, title: 'AI in Trading: The Future', category: 'Advanced Strategies', level: 'Advanced',
    views: 6500, timeAgo: '2 months ago', duration: '17:45', color: '#6366f1',
    desc: 'How artificial intelligence is transforming modern trading strategies.'
  }
];

const categoriesList = [
  { name: 'Technical Analysis', icon: HiOutlineChartBar, count: 24 },
  { name: 'Fundamental Analysis', icon: HiOutlineAcademicCap, count: 18 },
  { name: 'Options Trading', icon: HiOutlineArrowTrendingUp, count: 15 },
  { name: 'Risk Management', icon: HiOutlineShieldCheck, count: 12 },
  { name: 'Trading Psychology', icon: HiOutlineLightBulb, count: 10 },
  { name: 'Market Basics', icon: HiOutlineCube, count: 20 },
  { name: 'Advanced Strategies', icon: HiOutlineRocketLaunch, count: 16 },
];

const levels = [
  { name: 'Beginner', count: 28 },
  { name: 'Intermediate', count: 34 },
  { name: 'Advanced', count: 63 },
];

const ITEMS_PER_PAGE = 8;

const AdvancedLearning = ({ onBack }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedLevel, setSelectedLevel] = useState('All');
  const [sortBy, setSortBy] = useState('Latest');
  const [currentPage, setCurrentPage] = useState(1);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem('stocks4u_advanced_learning');
    let allVideos = [...videoData];
    if (saved) {
      allVideos = [...allVideos, ...JSON.parse(saved)];
    }
    setVideos(allVideos);
  }, []);

  const handleBack = () => {
    if (onBack) onBack();
    else navigate('/dashboard');
  };

  const filtered = useMemo(() => {
    let result = [...videos];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(v => v.title.toLowerCase().includes(q) || v.category.toLowerCase().includes(q));
    }
    if (selectedCategory !== 'All') result = result.filter(v => v.category === selectedCategory);
    if (selectedLevel !== 'All') result = result.filter(v => v.level === selectedLevel);
    if (sortBy === 'Most Viewed') result.sort((a, b) => b.views - a.views);
    return result;
  }, [search, selectedCategory, selectedLevel, sortBy, videos]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const formatViews = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : n;

  const getLevelClass = (lvl) => {
    if (lvl === 'Beginner') return 'al-level-beginner';
    if (lvl === 'Intermediate') return 'al-level-intermediate';
    return 'al-level-advanced';
  };

  return (
    <div className="al-page">
      {/* Back */}
      <button className="al-back-btn" onClick={handleBack}>
        <HiOutlineArrowLeft /> Back to Dashboard
      </button>

      {/* Hero */}
      <section className="al-hero">
        <div className="al-hero-left">
          <div className="al-pill"><span className="al-pill-dot" />LEARN & GROW</div>
          <h1 className="al-hero-title">Advanced Learning<br /><span className="al-gradient-text">Videos</span></h1>
          <p className="al-hero-desc">Master trading with in-depth video lessons from market experts.</p>
          <div className="al-features-row">
            <div className="al-feature-chip">
              <HiOutlineCheckCircle className="al-chip-icon" />
              <div><span className="al-chip-title">Expert Led</span><span className="al-chip-sub">Learn from industry pros</span></div>
            </div>
            <div className="al-feature-chip">
              <HiOutlineCheckCircle className="al-chip-icon" />
              <div><span className="al-chip-title">In-Depth</span><span className="al-chip-sub">Detailed lessons with examples</span></div>
            </div>
            <div className="al-feature-chip">
              <HiOutlineCheckCircle className="al-chip-icon" />
              <div><span className="al-chip-title">Practical</span><span className="al-chip-sub">Real market applications</span></div>
            </div>
            <div className="al-feature-chip">
              <HiOutlineCheckCircle className="al-chip-icon" />
              <div><span className="al-chip-title">At Your Pace</span><span className="al-chip-sub">Learn anytime, anywhere</span></div>
            </div>
          </div>
        </div>
        <div className="al-hero-right">
          <div className="al-hero-video-card">
            <div className="al-hero-video-bg" />
            <div className="al-hero-play-circle"><HiOutlinePlay className="al-hero-play-icon" /></div>
          </div>
        </div>
      </section>

      {/* Search Bar */}
      <section className="al-search-bar">
        <div className="al-search-input-wrap">
          <HiOutlineMagnifyingGlass className="al-search-icon" />
          <input className="al-search-input" placeholder="Search videos, topics or keywords..." value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }} />
          <button className="al-search-filter-btn"><HiOutlineAdjustmentsHorizontal /></button>
        </div>
        <div className="al-search-filters">
          <select className="al-filter-select" value={selectedCategory === 'All' ? 'All' : selectedCategory} onChange={e => { setSelectedCategory(e.target.value); setCurrentPage(1); }}>
            <option value="All">All Categories</option>
            {categoriesList.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
          <select className="al-filter-select" value={selectedLevel} onChange={e => { setSelectedLevel(e.target.value); setCurrentPage(1); }}>
            <option value="All">All Levels</option>
            {levels.map(l => <option key={l.name} value={l.name}>{l.name}</option>)}
          </select>
          <select className="al-filter-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="Latest">Sort by: Latest</option>
            <option value="Most Viewed">Sort by: Most Viewed</option>
          </select>
        </div>
      </section>

      {/* Main Content */}
      <section className="al-main">
        {/* Sidebar */}
        <aside className="al-sidebar">
          <div className="al-sidebar-section">
            <h3 className="al-sidebar-title">Categories</h3>
            <ul className="al-cat-list">
              {categoriesList.map(cat => {
                const Icon = cat.icon;
                const isActive = selectedCategory === cat.name;
                return (
                  <li key={cat.name} className={`al-cat-item ${isActive ? 'al-cat-active' : ''}`} onClick={() => { setSelectedCategory(isActive ? 'All' : cat.name); setCurrentPage(1); }}>
                    <Icon className="al-cat-icon" />
                    <span className="al-cat-name">{cat.name}</span>
                    <span className="al-cat-count">{cat.count}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="al-sidebar-section">
            <h3 className="al-sidebar-title">Level</h3>
            <ul className="al-cat-list">
              {levels.map(lvl => {
                const isActive = selectedLevel === lvl.name;
                return (
                  <li key={lvl.name} className={`al-cat-item ${isActive ? 'al-cat-active' : ''}`} onClick={() => { setSelectedLevel(isActive ? 'All' : lvl.name); setCurrentPage(1); }}>
                    <span className="al-cat-name">{lvl.name}</span>
                    <span className="al-cat-count">{lvl.count}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* CTA Card */}
          <div className="al-sidebar-cta">
            <h4 className="al-cta-title">Want to go deeper?</h4>
            <p className="al-cta-desc">Get premium access to exclusive webinars, courses & expert insights.</p>
            <button className="al-cta-btn">🚀 Upgrade Now</button>
          </div>
        </aside>

        {/* Video Grid */}
        <div className="al-grid-area">
          {paginated.length === 0 ? (
            <div className="al-empty">No videos found matching your criteria.</div>
          ) : (
            <div className="al-video-grid">
              {paginated.map(video => (
                <div key={video.id} className="al-video-card">
                  <div className="al-video-thumb" style={{ '--accent': video.color }}>
                    <div className="al-thumb-overlay">
                      <div className="al-thumb-play"><HiOutlinePlay /></div>
                    </div>
                    <span className="al-thumb-duration">{video.duration}</span>
                  </div>
                  <div className="al-video-body">
                    <h3 className="al-video-title">{video.title}</h3>
                    <span className={`al-video-level ${getLevelClass(video.level)}`}>{video.level}</span>
                    <div className="al-video-meta">
                      <span className="al-meta-item"><HiOutlineEye className="al-meta-icon" />{formatViews(video.views)} views</span>
                      <span className="al-meta-item"><HiOutlineClock className="al-meta-icon" />{video.timeAgo}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="al-pagination">
              {Array.from({ length: totalPages }, (_, i) => (
                <button key={i + 1} className={`al-page-btn ${currentPage === i + 1 ? 'al-page-active' : ''}`} onClick={() => setCurrentPage(i + 1)}>
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdvancedLearning;
